import { createSelector, createSlice, isRejectedWithValue } from '@reduxjs/toolkit'
import { RootState } from '../store'
import {
  employeeEnvironmentApi,
  engagementEnvironmentApi,
  mrfEnvironmentApi,
} from 'services/environment'
import {
  AlertEnvironmentResponse,
  EmployeeEnvironmentResponse,
  EngagementEnvironmentResponse,
  MrfEnvironmentTransformedResponse as MrfEnvironmentResponse,
} from 'services/environment/types'
import { alertEnvironmentApi } from 'services/environment/environment'

type ModuleEnvironmentState<T> = {
  isLoading: boolean
  isError: boolean
  data: T | undefined
}

export type EnvironmentState = {
  environment_engagement: ModuleEnvironmentState<EngagementEnvironmentResponse>
  environment_mrf: ModuleEnvironmentState<MrfEnvironmentResponse>
  environment_employee: ModuleEnvironmentState<EmployeeEnvironmentResponse>
  environment_alert: ModuleEnvironmentState<AlertEnvironmentResponse>
}

const engEnvValue = window.localStorage.getItem('environment_engagement')
const environmentEngagement =
  engEnvValue && engEnvValue !== 'undefined'
    ? (JSON.parse(engEnvValue) as EngagementEnvironmentResponse)
    : undefined

const mrfEnvValue = window.localStorage.getItem('environment_mrf')
const environmentMrf =
  mrfEnvValue && mrfEnvValue !== 'undefined'
    ? (JSON.parse(mrfEnvValue) as MrfEnvironmentResponse)
    : undefined

const employeeEnvValue = window.localStorage.getItem('environment_employee')
const environmentEmployee =
  employeeEnvValue && employeeEnvValue !== 'undefined'
    ? (JSON.parse(employeeEnvValue) as EmployeeEnvironmentResponse)
    : undefined

const alerEnvValue = window.localStorage.getItem('environment_alert')
const environmentAlert =
  alerEnvValue && alerEnvValue !== 'undefined'
    ? (JSON.parse(alerEnvValue) as AlertEnvironmentResponse)
    : undefined

const initialState: EnvironmentState = {
  environment_engagement: {
    isLoading: environmentEngagement ? false : true,
    data: environmentEngagement,
    isError: false,
  },
  environment_mrf: {
    isLoading: environmentMrf ? false : true,
    data: environmentMrf,
    isError: false,
  },
  environment_employee: {
    isLoading: environmentEmployee ? false : true,
    data: environmentEmployee,
    isError: false,
  },
  environment_alert: {
    isLoading: environmentAlert ? false : true,
    data: environmentAlert,
    isError: false,
  },
}

const environmentSlice = createSlice({
  name: 'environment',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      engagementEnvironmentApi.endpoints.getEnvironment.matchFulfilled,
      (state, { payload }) => {
        const data = payload
        state.environment_engagement.isLoading = false
        state.environment_engagement.isError = false
        state.environment_engagement.data = data
        localStorage.setItem('environment_engagement', JSON.stringify(data))
      },
    )
    builder.addMatcher(
      engagementEnvironmentApi.endpoints.getEnvironment.matchRejected,
      (state, action) => {
        if (isRejectedWithValue(action)) {
          state.environment_engagement.isLoading = false
          state.environment_engagement.isError = true
          state.environment_engagement.data = undefined
          localStorage.removeItem('environment_engagement')
          window.location.assign(`/app/not_authenticated`)
        }
      },
    )
    builder.addMatcher(
      engagementEnvironmentApi.endpoints.getEnvironment.matchPending,
      (state, _action) => {
        state.environment_engagement.isLoading = true
        state.environment_engagement.isError = false
        state.environment_engagement.data = undefined
      },
    )
    builder.addMatcher(
      mrfEnvironmentApi.endpoints.getEnvironment.matchFulfilled,
      (state, { payload }) => {
        const data = payload
        state.environment_mrf.isLoading = false
        state.environment_mrf.isError = false
        state.environment_mrf.data = data
        localStorage.setItem('environment_mrf', JSON.stringify(data))
      },
    )
    builder.addMatcher(
      mrfEnvironmentApi.endpoints.getEnvironment.matchRejected,
      (state, action) => {
        if (isRejectedWithValue(action)) {
          state.environment_mrf.isLoading = false
          state.environment_mrf.isError = true
          state.environment_mrf.data = undefined
          localStorage.removeItem('environment_mrf')
          window.location.assign(`/app/not_authenticated`)
        }
      },
    )
    builder.addMatcher(
      mrfEnvironmentApi.endpoints.getEnvironment.matchPending,
      (state, _action) => {
        state.environment_mrf.isLoading = true
        state.environment_mrf.isError = false
        state.environment_mrf.data = undefined
      },
    )
    builder.addMatcher(
      employeeEnvironmentApi.endpoints.getEnvironment.matchFulfilled,
      (state, { payload }) => {
        const data = payload
        state.environment_employee.isLoading = false
        state.environment_employee.isError = false
        state.environment_employee.data = data
        localStorage.setItem('environment_employee', JSON.stringify(data))
      },
    )
    builder.addMatcher(
      employeeEnvironmentApi.endpoints.getEnvironment.matchRejected,
      (state, action) => {
        if (isRejectedWithValue(action)) {
          state.environment_employee.isLoading = false
          state.environment_employee.isError = true
          state.environment_employee.data = undefined
          localStorage.removeItem('environment_employee')
          window.location.assign(`/app/not_authenticated`)
        }
      },
    )
    builder.addMatcher(
      employeeEnvironmentApi.endpoints.getEnvironment.matchPending,
      (state, _action) => {
        state.environment_employee.isLoading = true
        state.environment_employee.isError = false
        state.environment_employee.data = undefined
      },
    )
    builder.addMatcher(
      alertEnvironmentApi.endpoints.getEnvironment.matchFulfilled,
      (state, { payload }) => {
        const data = payload
        state.environment_alert.isLoading = false
        state.environment_alert.isError = false
        state.environment_alert.data = data
        localStorage.setItem('environment_alert', JSON.stringify(data))
      },
    )
    builder.addMatcher(
      alertEnvironmentApi.endpoints.getEnvironment.matchRejected,
      (state, action) => {
        if (isRejectedWithValue(action)) {
          state.environment_alert.isLoading = false
          state.environment_alert.isError = true
          state.environment_alert.data = undefined
          localStorage.removeItem('environment_alert')
          window.location.assign(`/app/not_authenticated`)
        }
      },
    )
    builder.addMatcher(
      alertEnvironmentApi.endpoints.getEnvironment.matchPending,
      (state, _action) => {
        state.environment_alert.isLoading = true
        state.environment_alert.isError = false
        state.environment_alert.data = undefined
      },
    )
  },
})

export default environmentSlice.reducer

const environmentState = (state: RootState) => state.environment

export const selectEnvironmentEngagement = createSelector(
  environmentState,
  ({ environment_engagement }) => environment_engagement,
)
export const selectEnvironmentMrf = createSelector(
  environmentState,
  ({ environment_mrf }) => environment_mrf,
)

export const selectEnvironmentEmployee = createSelector(
  environmentState,
  ({ environment_employee }) => environment_employee,
)

export const selectEnvironmentAlert = createSelector(
  environmentState,
  ({ environment_alert }) => environment_alert,
)
